import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CustomPagination from '../../../components/forms/Pagination';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, SvgIcon, TableSortLabel, Tooltip, useTheme } from '@mui/material';
import { Trash01 } from '../../../assets';
import { StockCheckScheduleDetail } from '../../../app/services/api/generated';
import useNswagClient from '../../../hooks/api/useNswagClient';
import SubmitModal from './ScheduleDeleteModal';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../../components/shared/useUser';
import { useContext } from 'react';
import { StockCheckType, StockCheckTypeLabels } from '../../../enums/StockCheckType';

export default function DenseTable(props: {
  readonly list: StockCheckScheduleDetail[], readonly storeID: string, readonly loadData: () => void, readonly searchTerm: string, readonly selectedType: number, readonly showAllStores?: boolean
}) {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { deleteSchedule } = useNswagClient();
  const [page, setPage] = React.useState(0);
  const [openModal, setOpenModal] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState(-1);
  const [data, setData] = React.useState(props.list);
  const rowsPerPage = 10;
  const { hasPermissionTo } = useContext(UserContext);
  //Header sort
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = React.useState<string>('name'); // Set the default column to be sorted

  const hasRightToEdit: boolean = hasPermissionTo(['StockCountScheduleWrite']) || hasPermissionTo(['StockCountAdmin']);

  const isAsc = order === 'asc';

  const handleSort = (property: string) => {
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    handleSearchChange();
  };

  const filteredData = props.list.filter(x =>
    x.name?.toLowerCase().includes(props.searchTerm.toLowerCase()) &&
    (props.selectedType === 0 || x.stockCheckType === props.selectedType),
  );


  const sortedData = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const comparator = (a: any, b: any) => {
      if (typeof a[orderBy] === 'string' && typeof b[orderBy] === 'string') {
        return isAsc ? a[orderBy].localeCompare(b[orderBy]) : b[orderBy].localeCompare(a[orderBy]);
      }

      if (a[orderBy] instanceof Date && b[orderBy] instanceof Date) {
        return isAsc ? a[orderBy].getTime() - b[orderBy].getTime() : b[orderBy].getTime() - a[orderBy].getTime();
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy]) as any;
    };
    return filteredData.sort(comparator);
  };

  const handleSearchChange = () => {
    setData(sortedData());
  };

  React.useEffect(() => {
    setData(sortedData());
  }, [props.searchTerm, props.selectedType]);

  const handleCancelModal = () => {
    setOpenModal(false);
  };

  const submitButtonHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    event.stopPropagation();
    if (!hasRightToEdit) {
      return;
    }
    setDeleteId(id);
    setOpenModal(true);
  };

  const handleConfirmModal = () => {
    if (!hasRightToEdit) {
      return;
    }

    deleteSchedule(props.storeID, deleteId).then(props.loadData);
  };

  const maxPages = () => {
    return Math.ceil(data.length / rowsPerPage);
  };

  const visibleRows = () => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  return (
    <Box sx={{
      border: '1px solid',
      borderColor: theme.palette.custom.gray[200],
    }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={order}
                  onClick={() => handleSort('name')}
                >
                  {t('name')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="left">
                <TableSortLabel
                  active={orderBy === 'isActive'}
                  direction={order}
                  onClick={() => handleSort('isActive')}
                >
                  {t('active')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="left">
                <TableSortLabel
                  active={orderBy === 'stockCheckType'}
                  direction={order}
                  onClick={() => handleSort('stockCheckType')}
                >
                  {t('stockCountType')}
                </TableSortLabel>
              </TableCell>
              {props.showAllStores && (
                <TableCell align="left">
                  <TableSortLabel
                    active={orderBy === 'storeNumber'}
                    direction={order}
                    onClick={() => handleSort('storeNumber')}
                  >
                    {t('stockcounts.storeName')}
                  </TableSortLabel>
                </TableCell>
              )}
              {hasRightToEdit && <TableCell align="left">
                {t('remove')}
              </TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows().map((row) => (
              <TableRow
                hover
                key={row.id}
                onClick={() => {
                  if (hasRightToEdit) {
                    navigate(`/create-edit-schedule-stock-count/${row.id}`);
                  }
                }}
                sx={{ cursor: hasRightToEdit ? 'pointer' : 'default' }}
              >
                <TableCell>
                  {row.name}
                </TableCell>
                <TableCell>
                  {String(row.isActive)}
                </TableCell>
                <TableCell>
                  {row.stockCheckType != null && StockCheckType[row.stockCheckType as StockCheckType]
                    ? StockCheckTypeLabels[row.stockCheckType as StockCheckType]
                    : t('noType')}
                </TableCell>
                {props.showAllStores && (
                  <TableCell>
                    {(row as StockCheckScheduleDetail & { storeNumber?: string }).storeNumber}
                  </TableCell>
                )}
                {hasRightToEdit && <TableCell>
                  <Tooltip title='Delete stock count schedule'
                    color='dark'
                    placement='top'>
                    <IconButton aria-label='Delete stock count schedule'
                      size='small'
                      onClick={(event) => submitButtonHandler(event, row.id ?? -1)}>
                      <SvgIcon color='error'><Trash01></Trash01></SvgIcon>
                    </IconButton>
                  </Tooltip>
                </TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomPagination
        page={page}
        setPage={setPage}
        maxPages={maxPages()}
        breakpointForChangeDisplay={120000}
      ></CustomPagination>

      {hasRightToEdit && <SubmitModal openModal={openModal}
        onConfirm={handleConfirmModal}
        onCancel={handleCancelModal}></SubmitModal>}
    </Box>
  );
}
